import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';

// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const GoldLoanEmiCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(500000);
  const [interestRate, setInterestRate] = useState(8);
  const [loanTenure, setLoanTenure] = useState(5);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal * monthlyInterest * Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let currentYear = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: (currentMonth + i) % 12 + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === '' ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === '' ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === '' ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ['Principal Amount', 'Total Interest'],
    datasets: [
      {
        label: 'EMI Breakdown',
        data: [loanAmount, totalInterest],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
        {
            question: "What is a Gold Loan EMI Calculator and how does it help me?",
            answer: "A Gold Loan EMI Calculator is an online tool that helps you estimate your monthly loan repayments before you borrow. It uses your loan amount, interest rate, and tenure to calculate fixed EMIs, helping you plan your finances better without surprises or miscalculations."
          },
          {
            question: "Is TheFinMaster’s Gold Loan EMI Calculator completely free?",
            answer: "Yes, the calculator is 100% free for all users. You can use it as many times as you want without any registration or charges."
          },
          {
            question: "Does the calculator work for all types of lenders—banks, NBFCs, etc.?",
            answer: "Absolutely. The calculator is designed to be lender-neutral. As long as you input the interest rate and tenure accurately, the results apply to any financial institution offering gold loans."
          },
          {
            question: "Can I calculate EMI for different tenures like 6 months, 12 months, or 2 years?",
            answer: "Yes, the calculator allows flexible input for tenure. Whether you want a short-term loan for 6 months or a longer repayment cycle up to 36 months, you can customize it easily."
          },
          {
            question: "Does it show total repayment and total interest separately?",
            answer: "Yes. In addition to EMI, it displays the total amount repayable, interest paid, and an amortization table—giving a clear picture of your loan cost."
          },
          {
            question: "Can I use the calculator for gold loan top-up or balance transfer?",
            answer: "While it's primarily for fresh loans, you can use it to estimate EMIs for balance transfers or top-ups by entering the new loan amount and updated interest rate."
          },
          {
            question: "Is the EMI calculation affected by the purity or weight of my gold?",
            answer: "Not directly. The calculator assumes you already know your eligible loan amount. However, purity and weight impact the eligible loan, which you can calculate separately before entering the figure into the EMI calculator."
          },
          {
            question: "What happens if I miss an EMI? Does this calculator factor in penalties?",
            answer: "No. The calculator does not include penalty charges. However, if you miss an EMI, lenders may charge late fees or higher interest. It’s best to use the calculator to pick a manageable EMI and avoid such situations."
          },
          {
            question: "Does the calculator consider prepayments or foreclosure?",
            answer: "This version doesn't factor in early payments or foreclosure, but the total interest displayed helps you understand how much you could potentially save by prepaying."
          },
          {
            question: "Can I download or print the amortization schedule?",
            answer: "While the tool on TheFinMaster currently doesn’t support direct downloads, you can take a screenshot or copy the data for reference. Printable or downloadable versions may be added in future updates."
          },
          {
            question: "Is the EMI fixed throughout the tenure?",
            answer: "Yes. For loans using a reducing balance method with fixed interest rate, the EMI remains constant. However, the share of interest and principal changes every month, as shown in the amortization table."
          },
          {
            question: "What’s the minimum and maximum tenure I can try on the calculator?",
            answer: "You can simulate EMIs for as little as 1 month up to 36 months or more, depending on your preference and lender offerings."
          },
          {
            question: "Can I use this calculator on my mobile phone?",
            answer: "Yes, TheFinMaster’s Gold Loan EMI Calculator is fully responsive and works smoothly on smartphones, tablets, and desktops without any app installation."
          },
          {
            question: "Is the calculator safe? Does it store any personal data?",
            answer: "The tool is completely secure and does not collect or store any personal or financial information. It’s built to be privacy-respecting and data-light."
          },
          {
            question: "Why should I use TheFinMaster’s calculator over others?",
            answer: "Unlike generic tools, TheFinMaster’s calculator is purpose-built for gold loans. It offers deep insights including EMI structure, interest component, amortization tables, and flexible inputs—all in a clean, ad-free interface tailored for Indian users."
          }
        ]
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
  return (
    <>
     <Helmet>
        <title>Gold Loan EMI Calculator – Interest & EMI Estimator</title>
        <meta  name='description' content='Estimate EMI and interest payable on your gold loan using our easy-to-use gold loan EMI calculator.'/>
         <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/gold-loan-emi-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" /> 
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Gold Loan EMI Calculator</span>
        </li> 
      
      </ol>
    </nav>
        <div className='py-4'>
    <div className="container mx-auto p-4 shadow-xl rounded-md">
      <h1 className="text-2xl font-bold mb-4">Gold Loan EMI Calculator</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Amount</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                value={loanAmount}
                onChange={handleLoanAmountChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">₹ {loanAmount.toLocaleString()}</span>
            </div>
            <input
              type="range"
              min="0"
              max="10000000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Rate of Interest (P.A.)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={interestRate}
                onChange={handleInterestRateChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{interestRate} %</span>
            </div>
            <input
              type="range"
              min="0"
              max="15"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold">Loan Tenure (Years)</label>
            <div className="flex items-center">
              <input
                type="number"
                min="0"
                max="30"
                value={loanTenure}
                onChange={handleLoanTenureChange}
                className="w-1/2 p-2 border rounded mr-2"
              />
              <span className="text-gray-700">{loanTenure} Yr</span>
            </div>
            <input
              type="range"
              min="0"
              max="30"
              value={loanTenure}
              onChange={(e) => setLoanTenure(parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <button
            onClick={calculateEMI}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Calculate
          </button>
        </div>
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-bold">Results</h2>
            <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
            <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
            <p>Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}</p>
            <p>Total Amount (Principal + Interest): ₹ {parseFloat(totalAmount).toLocaleString()}</p>
          </div>
          <div>
            <h2 className="text-xl font-bold">Breakdown</h2>
            <div className="flex justify-center">
              <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
        {amortizationSchedule.length > 0 && (
          <div className="flex flex-col space-y-4">
            {Array.from(new Set(amortizationSchedule.map(item => item.year))).map((year, idx) => (
              <div key={idx} className="mb-4 border rounded-md shadow-md">
                <div
                  className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${activeYear === year ? 'bg-blue-100' : ''}`}
                  onClick={() => handleYearToggle(year)}
                >
                  <span>{year}</span>
                  <span>{activeYear === year ? '-' : '+'}</span>
                </div>
                {activeYear === year && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-md shadow-md">
                      <thead>
                        <tr>
                          <th className="py-2 px-4 border-b">Month</th>
                          <th className="py-2 px-4 border-b">Principal Paid</th>
                          <th className="py-2 px-4 border-b">Interest Charged</th>
                          <th className="py-2 px-4 border-b">Total Payment</th>
                          <th className="py-2 px-4 border-b">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {amortizationSchedule.filter(item => item.year === year).map((item, idx) => (
                          <tr key={idx}>
                            <td className="py-2 px-4 border-b">{new Date(0, item.month - 1).toLocaleString('default', { month: 'short' })}</td>
                            <td className="py-2 px-4 border-b">₹ {item.principalPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.interestPaid.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.totalPayment.toFixed(2).toLocaleString()}</td>
                            <td className="py-2 px-4 border-b">₹ {item.balance.toFixed(2).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
     <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
     <div className="w-full md:w-2/3 p-4 mx-auto">
     <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            Plan Your Gold Loan Smartly with TheFinMaster’s EMI Calculator
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Gold loans are a fast and reliable financial solution that allow you to unlock the value of your gold assets without selling them. Whether you need urgent cash for medical emergencies, education, home repairs, or working capital, a gold loan can help bridge financial gaps without lengthy procedures.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            At TheFinMaster, we understand that every loan decision should be made with clarity. That’s why our Gold Loan EMI Calculator empowers you to plan your repayments smartly and efficiently. With just a few inputs, you get detailed insights into your monthly repayment obligations, total interest payable, and overall cost of the loan—helping you make informed borrowing decisions from day one.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Gold Loan EMI?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A Gold Loan EMI refers to the Equated Monthly Installment you pay to your lender as part of repaying your gold-backed loan. When you pledge gold ornaments or coins to borrow money, the lender provides funds based on the value of the gold, which you repay over time through fixed monthly payments.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Each EMI consists of:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Principal Component:</strong> The portion of the original loan amount.</li>
              <li><strong>Interest Component:</strong> The cost of borrowing charged by the lender.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              While some gold loans follow bullet repayment models, many lenders now offer flexible EMI-based gold loan repayment options, making it easier for salaried individuals and self-employed borrowers to manage their cash flows.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Choosing the right EMI structure ensures:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>You never miss a due date</li>
              <li>Your gold remains safe with the lender</li>
              <li>You repay the loan without financial stress</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use TheFinMaster’s Gold Loan EMI Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The Gold Loan EMI Calculator by TheFinMaster is designed to simplify the financial decision-making process for borrowers. It eliminates guesswork and gives instant, data-backed results that let you assess the affordability and suitability of a gold loan for your unique needs.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Key Reasons to Use It:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Instant EMI Projections:</strong> Calculate monthly payments in seconds.</li>
              <li><strong>Visual Clarity:</strong> View principal vs. interest breakdown in pie charts and schedules.</li>
              <li><strong>Cost Awareness:</strong> Understand the full cost of your loan before committing.</li>
              <li><strong>Risk Management:</strong> Avoid overborrowing by checking how EMIs fit into your budget.</li>
              <li><strong>Multiple Simulations:</strong> Try different loan amounts, interest rates, and tenures.</li>
              <li><strong>No Personal Details Required:</strong> No registration, no data tracking.</li>
              <li><strong>Mobile & Desktop Friendly:</strong> Use anytime, anywhere.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Whether you’re borrowing from a bank, NBFC, or cooperative society, this calculator brings transparency and control to your financial planning.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the Gold Loan EMI Calculator Work?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The EMI calculation is based on a standard mathematical formula that combines the principal, tenure, and interest rate into a single, fixed monthly payment.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>EMI Formula:</strong><br />
              EMI = [P × r × (1 + r)^n] / [(1 + r)^n – 1]
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = Principal loan amount</li>
              <li>r = Monthly interest rate (Annual Rate ÷ 12 ÷ 100)</li>
              <li>n = Loan tenure in months</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example Calculation:</strong><br />
              Let’s say you take a gold loan of ₹2,00,000 for a period of 1 year (12 months) at an annual interest rate of 10%.<br />
              P = ₹2,00,000<br />
              r = 10 ÷ 12 ÷ 100 = 0.00833<br />
              n = 12<br />
              EMI = [2,00,000 × 0.00833 × (1 + 0.00833)^12] ÷ [(1 + 0.00833)^12 – 1]<br />
              This gives an EMI of approximately ₹17,583 per month.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Over 12 months:<br />
              Total Repayment = ₹2,10,996<br />
              Total Interest = ₹10,996
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              With TheFinMaster’s calculator, these numbers are instantly available, letting you fine-tune inputs to find the most manageable loan structure.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Amortization Schedule
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              An Amortization Schedule provides a month-by-month breakdown of your EMI payments, clearly showing how much of each EMI goes toward interest and how much toward the principal.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Why It’s Useful:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Helps you understand the repayment journey.</li>
              <li>Shows how your interest outgo reduces with time.</li>
              <li>Enables better financial tracking.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Sample Amortization Table:</strong> (Example)<br />
              Loan Amount: ₹2,00,000 | Tenure: 12 months | Interest Rate: 10% p.a. | EMI: ₹17,583
            </p>
            {/* <div className="overflow-x-auto">
              <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">Month</th>
                    <th className="border px-4 py-2 text-left">EMI (₹)</th>
                    <th className="border px-4 py-2 text-left">Interest Paid (₹)</th>
                    <th className="border px-4 py-2 text-left">Principal Paid (₹)</th>
                    <th className="border px-4 py-2 text-left">Balance Outstanding (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-4 py-2">1</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">1,667</td>
                    <td className="border px-4 py-2">15,916</td>
                    <td className="border px-4 py-2">1,84,084</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">2</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">1,534</td>
                    <td className="border px-4 py-2">16,049</td>
                    <td className="border px-4 py-2">1,68,035</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">3</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">1,400</td>
                    <td className="border px-4 py-2">16,183</td>
                    <td className="border px-4 py-2">1,51,852</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">4</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">1,265</td>
                    <td className="border px-4 py-2">16,318</td>
                    <td className="border px-4 py-2">1,35,534</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">5</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">1,129</td>
                    <td className="border px-4 py-2">16,454</td>
                    <td className="border px-4 py-2">1,19,080</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">6</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">993</td>
                    <td className="border px-4 py-2">16,590</td>
                    <td className="border px-4 py-2">1,02,490</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">7</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">854</td>
                    <td className="border px-4 py-2">16,729</td>
                    <td className="border px-4 py-2">85,761</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">8</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">715</td>
                    <td className="border px-4 py-2">16,868</td>
                    <td className="border px-4 py-2">68,893</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">9</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">574</td>
                    <td className="border px-4 py-2">17,009</td>
                    <td className="border px-4 py-2">51,884</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">10</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">432</td>
                    <td className="border px-4 py-2">17,151</td>
                    <td className="border px-4 py-2">34,733</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">11</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">290</td>
                    <td className="border px-4 py-2">17,293</td>
                    <td className="border px-4 py-2">17,440</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">12</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">146</td>
                    <td className="border px-4 py-2">17,437</td>
                    <td className="border px-4 py-2">0</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            {/* Desktop Table View */}
<div className="hidden md:block overflow-x-auto">
  <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
    <thead>
      <tr className="bg-gray-100">
        <th className="border px-4 py-2 text-left">Month</th>
        <th className="border px-4 py-2 text-left">EMI (₹)</th>
        <th className="border px-4 py-2 text-left">Interest Paid (₹)</th>
        <th className="border px-4 py-2 text-left">Principal Paid (₹)</th>
        <th className="border px-4 py-2 text-left">Balance Outstanding (₹)</th>
      </tr>
    </thead>
    <tbody>
                  <tr>
                    <td className="border px-4 py-2">1</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">1,667</td>
                    <td className="border px-4 py-2">15,916</td>
                    <td className="border px-4 py-2">1,84,084</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">2</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">1,534</td>
                    <td className="border px-4 py-2">16,049</td>
                    <td className="border px-4 py-2">1,68,035</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">3</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">1,400</td>
                    <td className="border px-4 py-2">16,183</td>
                    <td className="border px-4 py-2">1,51,852</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">4</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">1,265</td>
                    <td className="border px-4 py-2">16,318</td>
                    <td className="border px-4 py-2">1,35,534</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">5</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">1,129</td>
                    <td className="border px-4 py-2">16,454</td>
                    <td className="border px-4 py-2">1,19,080</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">6</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">993</td>
                    <td className="border px-4 py-2">16,590</td>
                    <td className="border px-4 py-2">1,02,490</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">7</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">854</td>
                    <td className="border px-4 py-2">16,729</td>
                    <td className="border px-4 py-2">85,761</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">8</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">715</td>
                    <td className="border px-4 py-2">16,868</td>
                    <td className="border px-4 py-2">68,893</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">9</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">574</td>
                    <td className="border px-4 py-2">17,009</td>
                    <td className="border px-4 py-2">51,884</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">10</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">432</td>
                    <td className="border px-4 py-2">17,151</td>
                    <td className="border px-4 py-2">34,733</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">11</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">290</td>
                    <td className="border px-4 py-2">17,293</td>
                    <td className="border px-4 py-2">17,440</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">12</td>
                    <td className="border px-4 py-2">17,583</td>
                    <td className="border px-4 py-2">146</td>
                    <td className="border px-4 py-2">17,437</td>
                    <td className="border px-4 py-2">0</td>
                  </tr>
                </tbody>
  </table>
</div>

{/* Mobile Card View */}
<div className="md:hidden mt-4 space-y-4">
  {[
    ["1", "17,583", "1,667", "15,916", "1,84,084"],
    ["2", "17,583", "1,534", "16,049", "1,68,035"],
    ["3", "17,583", "1,400", "16,183", "1,51,852"],
    ["4", "17,583", "1,265", "16,318", "1,35,534"],
    ["5", "17,583", "1,129", "16,454", "1,19,080"],
    ["6", "17,583", "993", "16,590", "1,02,490"],
    ["7", "17,583", "854", "16,729", "85,761"],
    ["8", "17,583", "715", "16,868", "68,893"],
    ["9", "17,583", "574", "17,009", "51,884"],
    ["10", "17,583", "432", "17,151", "34,733"],
    ["11", "17,583", "290", "17,293", "17,440"],
    ["12", "17,583", "146", "17,437", "0"],
  ].map(([month, emi, interest, principal, balance], index) => (
    <div key={index} className="border rounded-lg p-4 bg-white shadow-sm text-sm">
      <p><span className="font-medium">Month:</span> {month}</p>
      <p><span className="font-medium">EMI (₹):</span> {emi}</p>
      <p><span className="font-medium">Interest Paid (₹):</span> {interest}</p>
      <p><span className="font-medium">Principal Paid (₹):</span> {principal}</p>
      <p><span className="font-medium">Balance Outstanding (₹):</span> {balance}</p>
    </div>
  ))}
</div>

            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              With the EMI calculator, this table is generated instantly to guide your planning process.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using TheFinmaster’s Gold Loan EMI Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The calculator goes beyond just numbers. It is designed to empower you with financial confidence and clarity.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Major Benefits:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Clarity Before Commitment:</strong> Know your repayment plan before you borrow.</li>
              <li><strong>Avoid Financial Strain:</strong> Borrow only what you can comfortably repay.</li>
              <li><strong>Customized Planning:</strong> Compare tenures and interest rates to find your best match.</li>
              <li><strong>Save Time:</strong> No manual calculations or paperwork needed.</li>
              <li><strong>Risk Mitigation:</strong> Understand repayment timelines to avoid defaults.</li>
              <li><strong>Budgeting Help:</strong> Helps you align loan EMIs with other expenses.</li>
              <li><strong>Prepayment Planning:</strong> Know how early payments can reduce interest burden.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Factors That Affect Your Gold Loan EMI
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Several variables can change your EMI obligation. Understanding them ensures you are financially prepared.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Key Influencing Factors:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Loan Amount:</strong> Higher loans = higher EMIs.</li>
              <li><strong>Interest Rate:</strong> Varies by lender, credit history, and gold purity.</li>
              <li><strong>Tenure:</strong> Shorter tenure = higher EMIs, lower interest.</li>
              <li><strong>Gold Valuation:</strong> The purity and weight of pledged gold influence loan eligibility.</li>
              <li><strong>Type of Interest:</strong> Flat rate vs. reducing balance impacts EMI calculation.</li>
              <li><strong>Prepayment Options:</strong> Flexible prepayment can lower the overall cost.</li>
              <li><strong>Processing Fees & Charges:</strong> While not part of the EMI, these affect total outgo.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              By adjusting these parameters in the calculator, you can determine the most cost-effective borrowing strategy.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Who Should Use a Gold Loan EMI Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Anyone considering a gold loan can benefit from using the calculator, but it’s especially helpful for:
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Ideal Users:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Salaried Professionals:</strong> To assess monthly financial commitments.</li>
              <li><strong>Small Business Owners:</strong> To plan for short-term funding without asset sale.</li>
              <li><strong>Students & Parents:</strong> To manage education expenses with planned repayments.</li>
              <li><strong>Farmers & Traders:</strong> Who often rely on seasonal income and need flexible EMI planning.</li>
              <li><strong>Home Renovators:</strong> Seeking quick funds for repairs or upgrades.</li>
              <li><strong>Medical Emergency Planners:</strong> Where urgency meets repayment planning.</li>
              <li><strong>Women Entrepreneurs:</strong> Leveraging gold for startup capital with repayment visibility.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The calculator ensures that borrowers of all types stay financially informed.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Gold Loan vs. Personal Loan EMI – A Quick Comparison
            </h1>
            {/* <div className="overflow-x-auto">
              <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">Feature</th>
                    <th className="border px-4 py-2 text-left">Gold Loan EMI</th>
                    <th className="border px-4 py-2 text-left">Personal Loan EMI</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-4 py-2">Collateral</td>
                    <td className="border px-4 py-2">Gold jewellery/coins</td>
                    <td className="border px-4 py-2">Unsecured</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Interest Rate</td>
                    <td className="border px-4 py-2">7%–15% (lower)</td>
                    <td className="border px-4 py-2">11%–24% (higher)</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Tenure Flexibility</td>
                    <td className="border px-4 py-2">3 to 36 months</td>
                    <td className="border px-4 py-2">12 to 60 months</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Processing Time</td>
                    <td className="border px-4 py-2">Fast (less than 24 hours)</td>
                    <td className="border px-4 py-2">Slower</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">Ideal For</td>
                    <td className="border px-4 py-2">Asset-backed, quick funds</td>
                    <td className="border px-4 py-2">No-assets borrowers</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            {/* Desktop Table */}
<div className="hidden md:block mt-4">
  <table className="w-full text-lg text-gray-600 border-collapse">
    <thead>
      <tr className="bg-gray-100">
        <th className="border px-4 py-2 text-left">Feature</th>
        <th className="border px-4 py-2 text-left">Gold Loan EMI</th>
        <th className="border px-4 py-2 text-left">Personal Loan EMI</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td className="border px-4 py-2">Collateral</td>
        <td className="border px-4 py-2">Gold jewellery/coins</td>
        <td className="border px-4 py-2">Unsecured</td>
      </tr>
      <tr>
        <td className="border px-4 py-2">Interest Rate</td>
        <td className="border px-4 py-2">7%–15% (lower)</td>
        <td className="border px-4 py-2">11%–24% (higher)</td>
      </tr>
      <tr>
        <td className="border px-4 py-2">Tenure Flexibility</td>
        <td className="border px-4 py-2">3 to 36 months</td>
        <td className="border px-4 py-2">12 to 60 months</td>
      </tr>
      <tr>
        <td className="border px-4 py-2">Processing Time</td>
        <td className="border px-4 py-2">Fast (less than 24 hours)</td>
        <td className="border px-4 py-2">Slower</td>
      </tr>
      <tr>
        <td className="border px-4 py-2">Ideal For</td>
        <td className="border px-4 py-2">Asset-backed, quick funds</td>
        <td className="border px-4 py-2">No-assets borrowers</td>
      </tr>
    </tbody>
  </table>
</div>

{/* Mobile Cards */}
<div className="md:hidden space-y-4 mt-4">
  {[
    { feature: "Collateral", gold: "Gold jewellery/coins", personal: "Unsecured" },
    { feature: "Interest Rate", gold: "7%–15% (lower)", personal: "11%–24% (higher)" },
    { feature: "Tenure Flexibility", gold: "3 to 36 months", personal: "12 to 60 months" },
    { feature: "Processing Time", gold: "Fast (less than 24 hours)", personal: "Slower" },
    { feature: "Ideal For", gold: "Asset-backed, quick funds", personal: "No-assets borrowers" },
  ].map((item, index) => (
    <div key={index} className="border rounded-lg p-4 shadow-sm bg-white">
      <p className="font-semibold text-gray-700 mb-2">{item.feature}</p>
      <div className="text-sm text-gray-600">
        <p><span className="font-medium">Gold Loan EMI:</span> {item.gold}</p>
        <p><span className="font-medium">Personal Loan EMI:</span> {item.personal}</p>
      </div>
    </div>
  ))}
</div>

          </div>
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
   </>
  );
};

export default GoldLoanEmiCalculator;
